import React, {useEffect, useState} from "react"
import Plot from 'react-plotly.js'
import {alpha, Box} from "@mui/material"
import PropTypes from 'prop-types'
import SkeletonChart from "../../../../components/skeleton-chart"
import {generateSingleColor} from "../../../../utils/colors"

export const CHART_TYPE_LINE = "line"
export const CHART_TYPE_SCATTER = "scatter"
export const CHART_TYPE_BOX_PLOT = "boxplot"
export const CHART_TYPE_BOX_PLOT_SCATTER = "boxplot_scatter"

const MarketToMarketChart = ({xValue, data, loading, labelX, labelY, maxX}) => {
    const [plotData, setPlotData] = useState([])
    const [layoutData, setLayoutData] = useState(null)

    useEffect(() => {
        loadPlotData()
        loadLayoutData()
    }, [data, xValue, labelX, labelY, maxX])


    const getLinhaGrafico = (item) => {
        const {data, label, tooltip, color, text} = item
        const {title, x, y} = tooltip

        return {
            mode: 'lines+text',
            name: label,
            x: xValue.map(String),
            y: data,
            text: text ? [...Array(xValue.length - 1).fill(''), `<b>${text}</b>`] : null,
            textposition: 'center right',
            textfont: {
                size: 14,
                width: 'bold',
                color: color
            },
            cliponaxis: false,
            line: {
                shape: 'spline',
                smoothing: 0.4,
                color: color || generateSingleColor(),
                width: 2,
            },
            hovertemplate:
                `${title || ''} : ${label} <br>` +
                `${x || ''} : %{x} <br>` +
                `${y || ''} : %{y:.4f} <br>` +
                `<extra></extra>`
        }
    }

    const geraScatterGrafico = (item) => {
        return {
            mode: 'markers',
            name: item.label,
            x: item.data.map(d => d.x.toString()),
            y: item.data.map(d => d.y),
            marker: {
                color: alpha(item.color, 0.2) || generateSingleColor(),
                size: 20,
                symbol: 'triangle-up',
                line: {
                    color: alpha(item.color, 0.5) || generateSingleColor(),
                    width: 1
                }
            },
            hovertemplate: `${item.tooltip || ''}` + `<extra></extra>`
        }
    }

    const getBoxplotGrafico = (item, chartType) => {
        const {label, data, x, tooltip} = item

        const color = item.color || generateSingleColor(label)
        const {Min, Max, Q1, Q3, Median} = data
        const yValues = [Min, Q1, Median, Q3, Max]
        const xCategory = String(x)
        let box = []

        if (chartType === CHART_TYPE_BOX_PLOT) {
            box = {
                type: 'box',
                name: item.label,
                x: Array(yValues.length).fill(xCategory.toString()),
                y: yValues,
                width: 0.02,
                boxpoints: false,
                marker: {color: color},
                line: {color: color},
                hoverinfo: 'skip',
            }
        }

        const scatterStats = {
            type: 'scatter',
            mode: 'markers',
            name: item.label,
            x: [xCategory],
            y: [Median],
            marker: {
                opacity: chartType === CHART_TYPE_BOX_PLOT ? 0 : 1,
                size: chartType === CHART_TYPE_BOX_PLOT ? 40 : 20,
                color: alpha(color, 0.2),
                symbol: 'square',
            },
            customdata: [yValues],
            hovertemplate: `${tooltip || ''}` + `<extra></extra>`,
            showlegend: false,
        }

        return [box, scatterStats]
    }

    const loadPlotData = () => {
        let newPlotData = data.flatMap(item => {
            switch (item.type) {
                case CHART_TYPE_LINE:
                    return getLinhaGrafico(item)
                case CHART_TYPE_SCATTER:
                    return geraScatterGrafico(item)
                case CHART_TYPE_BOX_PLOT:
                    return getBoxplotGrafico(item, CHART_TYPE_BOX_PLOT)
                case CHART_TYPE_BOX_PLOT_SCATTER:
                    return getBoxplotGrafico(item, CHART_TYPE_BOX_PLOT_SCATTER)
                default:
                    return []
            }
        }).filter(Boolean)

        setPlotData(newPlotData)
    }

    const loadLayoutData = () => {
        const fontAxis = {
            family: "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
            size: 12
        }

        setLayoutData({
            autosize: true,
            showlegend: false,
            margin: {l: 60, r: 20, t: 40, b: 60},
            height: window.innerHeight * 0.6,
            xaxis: {
                title: {
                    text: labelX || "",
                    font: fontAxis,
                    yref: 'paper',
                    automargin: true,
                },
                zeroline: true,
                zerolinecolor: 'rgba(0,0,0,0.2)',
                showgrid: true,
                dtick: 0.5,
            },
            yaxis: {
                title: {
                    text: labelY || "",
                    font: fontAxis,
                    yref: 'paper',
                    automargin: true,
                },
                zeroline: true,
                zerolinecolor: 'rgba(0,0,0,0.2)',
                showgrid: true,
                rangemode: 'tozero',
            },
            legend: {
                orientation: 'h',
                y: -0.2,
                font: {
                    size: 14,
                    family: 'Arial, sans-serif',
                    color: '#000',
                }
            },
            hovermode: 'closest'
        })
    }

    return (
        <>
            {loading ?
                <>
                    <br/>
                    <SkeletonChart/>
                </>
                :
                <Box sx={{height: '60vh', display: 'flex', flexDirection: 'column'}}>
                    <Box sx={{flexGrow: 1, position: 'relative'}}>
                        <Plot
                            data={plotData}
                            layout={layoutData}
                            useResizeHandler={true}
                            style={{width: '100%', height: '100%'}}
                            config={{
                                responsive: true,
                                displayModeBar: true,
                                displaylogo: false,
                                modeBarButtonsToRemove: ['sendDataToCloud'],
                            }}
                        />
                    </Box>
                </Box>
            }
        </>
    )
}

MarketToMarketChart.propTypes = {
    xValue: PropTypes.array,
    data: PropTypes.array,
    loading: PropTypes.bool,
    labelX: PropTypes.string,
    labelY: PropTypes.string,
    maxX: PropTypes.number
}

export default MarketToMarketChart